import React from 'react';

import { cssMap } from '@compiled/react';
import noop from 'lodash/noop';
import { token } from '@atlaskit/tokens';

import ButtonGroup from '@atlaskit/button/button-group';
import Button, { LinkButton } from '@atlaskit/button/new';
import Form, { FormHeader } from '@atlaskit/form';

import { Text, Stack } from '@atlaskit/primitives/compiled';
import { useIntl } from '@atlassian/jira-intl';

import { lookupTypeByTypeId } from '../../../../common/application-types.tsx';
import ProductCard from '../../../common/product-card/index.tsx';
import {
	getDefaultCreationState,
	generateApplinksCreationRedirectionUrl,
} from '../../../../common/interchange.tsx';
import type { StageReciprocalPromptProps } from '../../../../common/types.tsx';
import { StatusLozenge } from '../../../common/status/lozenge/StatusLozenge.tsx';
import { ERRORS } from '../../../../common/status/errors.tsx';
import { SubpageTemplate } from '../template/SubpageTemplate.tsx';

const styles = cssMap({
	linkDisplay: {
		marginTop: token('space.200'),
		marginBottom: token('space.200'),
	},
	fullContainer: {
		height: '100%',
	},
});

type ReciprocalPromptSubpageProps = {
	state: StageReciprocalPromptProps;
};

const ReciprocalPromptSubpage = (props: ReciprocalPromptSubpageProps): React.JSX.Element => {
	useIntl();

	const thisApplicationManifest = props.state.localManifest;
	const targetApplicationManifest = props.state.remoteManifest;

	const targetCloudId = props.state.remoteManifest.id;
	const thisBaseUrl = thisApplicationManifest.url;
	const thisBaseTypeId = thisApplicationManifest.typeId;

	const creationState = getDefaultCreationState(thisBaseUrl, thisBaseTypeId, targetCloudId);

	const reciprocalLinkUrl = generateApplinksCreationRedirectionUrl(
		targetApplicationManifest.url,
		targetApplicationManifest.typeId,
		creationState,
	);

	return (
		<Form<{ username: string; password: string; remember: boolean }>
			onSubmit={noop}
			xcss={styles.fullContainer}
		>
			{({ formProps }) => (
				<form {...formProps}>
					<SubpageTemplate
						variant={props.state.variant}
						kind={props.state.kind}
						buttons={
							<ButtonGroup label="Form submit options">
								{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
								<Button appearance="subtle">Cancel</Button>
								<LinkButton href={reciprocalLinkUrl} appearance="primary">
									{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
									{`Continue on ${targetApplicationManifest.name}`}
								</LinkButton>
							</ButtonGroup>
						}
						content={
							<Stack>
								<FormHeader title="Create reciprocal link">
									{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
									<Text>
										A link from Jira to{' '}
										{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
										{targetApplicationManifest.name} has been established. You will need to navigate
										to {/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
										{targetApplicationManifest.name} to complete the link.
									</Text>
								</FormHeader>

								<Stack xcss={styles.linkDisplay} space="space.200">
									<ProductCard
										// TODO: Determine if the type is DC or Cloud
										applicationType={lookupTypeByTypeId(targetApplicationManifest.typeId, false)}
										displayUrl={targetApplicationManifest.url}
										heading="Application Link"
										statusLozenge={
											<StatusLozenge kind="error" err={ERRORS.CONFIGURATION_COMPLETE} />
										}
									/>
								</Stack>
							</Stack>
						}
					/>
				</form>
			)}
		</Form>
	);
};

export default ReciprocalPromptSubpage;
